/**
 * `/api/referrals` payloads — referral code lookup, KOL history, and the
 * referral forest.
 *
 * `history` and `tree` are public, cross-origin endpoints ported from retired
 * Cloudflare Workers, so their envelopes (`{ success, data }` / `{ success,
 * error }`) are part of the contract with consumers that were never updated.
 */
import { z } from "zod";

import { suiAddressLooseSchema } from "../primitives";

/** Query for `GET /api/referrals/get-referral-code-by-wallet-address`. */
export const getReferralCodeQuerySchema = z.object({
  address: suiAddressLooseSchema,
});

/** Resolved referral-code query. */
export type GetReferralCodeQuery = z.infer<typeof getReferralCodeQuerySchema>;

/** Response for `GET /api/referrals/get-referral-code-by-wallet-address`. */
export const getReferralCodeResponseSchema = z.object({
  referralCode: z.string().nullable(),
  error: z.string().optional(),
});

/** Resolved referral-code response. */
export type GetReferralCodeResponse = z.infer<typeof getReferralCodeResponseSchema>;

/** Referral codes are short; bound the input so a huge value cannot burn CPU. */
export const MAX_REFERRAL_CODE_LENGTH = 512;

/** Query for `GET /api/referrals/history`. */
export const referralHistoryQuerySchema = z.object({
  /**
   * The ENCRYPTED code for a public caller, the PLAINTEXT code for an admin one
   * (presence of `x-api-key` / `Bearer` selects the encrypt branch).
   */
  code: z.string().min(1).max(MAX_REFERRAL_CODE_LENGTH),
});

/** Resolved referral-history query. */
export type ReferralHistoryQuery = z.infer<typeof referralHistoryQuerySchema>;

/** One license bought through a referral code. */
export const referredLicenseSchema = z.object({
  license_id: z.string(),
  owner: z.string().nullable(),
  /** TEXT in the database (u64 from chain), so a string here too. */
  current_price: z.string().nullable(),
  timestamp: z.string().nullable(),
});

/** Resolved referred-license row. */
export type ReferredLicense = z.infer<typeof referredLicenseSchema>;

/** Public branch payload: the licenses behind a decrypted referral code. */
export const referralHistoryDataSchema = z.object({
  referral_code: z.string(),
  referrer_wallet_address: z.string().optional(),
  licenses: z.array(referredLicenseSchema),
});

/** Resolved referral-history payload. */
export type ReferralHistoryData = z.infer<typeof referralHistoryDataSchema>;

/** Admin branch payload: the encrypted form of a plaintext code. */
export const encryptedCodeDataSchema = z.object({
  original_code: z.string(),
  encrypted_code: z.string(),
});

/** Resolved encrypted-code payload. */
export type EncryptedCodeData = z.infer<typeof encryptedCodeDataSchema>;

/** Response for `GET /api/referrals/history`. */
export const getReferralHistoryResponseSchema = z.union([
  z.object({
    success: z.literal(true),
    data: z.union([referralHistoryDataSchema, encryptedCodeDataSchema]),
  }),
  z.object({ success: z.literal(false), error: z.string() }),
]);

/** Resolved referral-history response. */
export type GetReferralHistoryResponse = z.infer<typeof getReferralHistoryResponseSchema>;

/** Aggregate counters attached to every node of the referral forest. */
export const treeNodeMetricsSchema = z.object({
  /** Licenses sold to this wallet's direct referrals. */
  direct_licenses: z.number(),
  direct_referrals: z.number(),
  /** Licenses across the whole subtree. */
  total_licenses: z.number(),
  total_referrals: z.number(),
  /** Direct sales volume. Prices are u64 micro-USDC, hence the /1e6 upstream. */
  direct_amount_usdc: z.number(),
  total_amount_usdc: z.number(),
});

/** Resolved node metrics. */
export type TreeNodeMetrics = z.infer<typeof treeNodeMetricsSchema>;

/**
 * One node of the referral forest.
 *
 * Recursive, so the type is declared first and `z.lazy` carries it — zod cannot
 * infer a self-referencing shape. `wallet` is `unknown` in the original Worker
 * port because the column is nullable and the traversal keys on whatever value
 * is there; it is narrowed to `string | null` here, which is what the column
 * actually holds.
 */
export interface TreeNode {
  wallet: string | null;
  referral_code: string | null;
  licenses_held: number;
  children: TreeNode[];
  is_cycle?: boolean;
  truncated?: boolean;
  metrics: TreeNodeMetrics;
}

/** Runtime schema for {@link TreeNode}. */
export const treeNodeSchema: z.ZodType<TreeNode> = z.lazy(() =>
  z.object({
    wallet: z.string().nullable(),
    referral_code: z.string().nullable(),
    licenses_held: z.number(),
    children: z.array(treeNodeSchema),
    is_cycle: z.boolean().optional(),
    truncated: z.boolean().optional(),
    metrics: treeNodeMetricsSchema,
  }),
);

/** Query for `GET /api/referrals/tree`. */
export const referralTreeQuerySchema = z.object({
  /** Absent means "every root". Present means "this wallet's subtree". */
  wallet: suiAddressLooseSchema.optional(),
  /** `"true"` expands past the default depth, still under the hard ceilings. */
  full_tree: z.string().optional(),
});

/** Resolved referral-tree query. */
export type ReferralTreeQuery = z.infer<typeof referralTreeQuerySchema>;

/**
 * Response for `GET /api/referrals/tree`.
 *
 * `data` is a single node when `?wallet=` was given and an array of roots
 * otherwise. `truncated` appears only when a traversal bound was hit.
 */
export const getReferralTreeResponseSchema = z.union([
  z.object({
    success: z.literal(true),
    data: z.union([treeNodeSchema, z.array(treeNodeSchema)]),
    truncated: z.literal(true).optional(),
  }),
  z.object({
    success: z.literal(false),
    error: z.string(),
    timestamp: z.string().optional(),
  }),
]);

/** Resolved referral-tree response. */
export type GetReferralTreeResponse = z.infer<typeof getReferralTreeResponseSchema>;
